import React, { useEffect, useState } from 'react';
import { Table, Space, Tooltip, Select, Modal, message } from 'antd';
import { EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../../firebase-config';
import FormModule from '../FormModule/FormModule';
import ButtonComponent from '../../../CssComponents/ButtonComponent/ButtonComponent';
import styles from '../../../../styles/LmsList.module.css';
import arLogo from "../../../../logo.webp"

function ListModule() {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        const fetchModules = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'lms_module'));
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                console.log('Fetched modules:', data);
                console.log('Module types:', data.map(m => ({ name: m.moduleName, type: m.moduleType })));
                setModules(data);
            } catch (error) {
                console.error('Error fetching modules:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchModules();
    }, []);

    const refreshModules = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'lms_module'));
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            console.log('Refreshed modules:', data);
            setModules(data);
        } catch (error) {
            console.error('Error refreshing modules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateModule = () => {
        setInitialValues({});
        setShowForm(true);
    };

    const handleEditModule = (module) => {
        setInitialValues(module);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        refreshModules(); // Refresh data when form closes
    };

    // Add delete handler
    const handleDeleteModule = async (moduleId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this module?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    await deleteDoc(doc(db, 'lms_module', moduleId));
                    message.success('Module deleted successfully');
                    setModules(prev => prev.filter(m => m.id !== moduleId));
                } catch (error) {
                    message.error('Failed to delete module');
                }
            },
        });
    };

    const columns = [
        {
            title: 'Banner',
            dataIndex: 'moduleBanner',
            key: 'moduleBanner',
            render: url => (
                <img
                    src={url || arLogo}
                    alt="Module Banner"
                    style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4 }}
                />
            ),
        },
        {
            title: 'Module Name',
            dataIndex: 'moduleName',
            key: 'moduleName',
            render: text => text || 'Untitled Module',
        },
        {
            title: 'Description',
            dataIndex: 'moduleDescription',
            key: 'moduleDescription',
            render: text => text || 'No description available.',
        },
        {
            title: 'Type',
            dataIndex: 'moduleType',
            key: 'moduleType',
            render: type => {
                console.log('Rendering type:', type);
                if (type === 'digital') return 'Digital';
                if (type === 'live') return 'Live';
                return type ? type : 'Not Set';
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <EditOutlined
                            onClick={() => handleEditModule(record)}
                        />
                    </Tooltip>
                    <Tooltip title="View">
                        <EyeOutlined
                            onClick={() => handleEditModule(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <DeleteOutlined
                            onClick={() => handleDeleteModule(record.id)}
                            style={{ color: 'red' }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // Filtering logic with debugging
    const filteredModules = filterType === 'all'
        ? modules
        : modules.filter(m => {
            console.log(`Filtering: ${m.moduleName} - Type: ${m.moduleType} - Filter: ${filterType}`);
            return m.moduleType === filterType;
        });
    
    console.log(`Filter type: ${filterType}, Total modules: ${modules.length}, Filtered: ${filteredModules.length}`);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.heading}>Available Modules</div>
                <ButtonComponent
                    onClick={handleCreateModule}
                    buttonText={"Create New Module"}
                />
            </div>
            {/* Filter Dropdown */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>Filter by Type:</span>
                <Select
                    value={filterType}
                    onChange={setFilterType}
                    style={{ width: 160 }}
                    options={[
                        { label: 'All', value: 'all' },
                        { label: 'Digital', value: 'digital' },
                        { label: 'Live', value: 'live' },
                    ]}
                />
            </div>
            <Table
                dataSource={filteredModules}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 8 }}
            />
            {showForm && (
                <FormModule
                    initialValues={initialValues}
                    setInitialValues={setInitialValues}
                    showForm={showForm}
                    onClose={handleFormClose}
                />
            )}
        </div>
    );
}

export default ListModule;
